/** Rare, finite cascade amphitheatres derived from already valid river routes.
 * The route remains downhill; all terraces are shared physical terrain. */
import type {RiverPoint,RiverPolyline} from './rivers.js';
import {riverPointBetween,waterGravity,type RiverFall} from './riverFalls.js';
import type {RiverPoolTerrain} from './riverPools.js';
import * as pmath from './pmath.js';
export const ESCARPMENT_MAX_CUT_M=36;
export interface RiverEscarpment {
 readonly id:string;readonly riverId:number;readonly anchorId:string;
 readonly center:readonly[number,number,number];readonly radiusM:number;
 readonly widthM:number;readonly stages:readonly RiverFall[];
}
export interface EscarpmentSample {heightM:number;rock:number;terrace:number;weight:number}
export const escarpmentSample=():EscarpmentSample=>({heightM:0,rock:0,terrace:0,weight:0});
const smooth=(a:number,b:number,x:number):number=>{const k=Math.max(0,Math.min(1,(x-a)/(b-a)));return k*k*(3-2*k);};
const shifted=(p:RiverPoint,R:number,along:number,cross:number):[number,number,number]=>{
 const sx=p.fy*p.z-p.fz*p.y,sy=p.fz*p.x-p.fx*p.z,sz=p.fx*p.y-p.fy*p.x;
 const x=p.x+(p.fx*along+sx*cross)/R,y=p.y+(p.fy*along+sy*cross)/R,z=p.z+(p.fz*along+sz*cross)/R,l=pmath.hypot(x,y,z);return[x/l,y/l,z/l];
};
/** Conservative initial distribution: at most one formation on a living world,
 * requiring a long, straight, steep inland descent and three supported drops.
 * No seed/planet allowlist or runtime camera information participates. */
export function buildRiverEscarpment(rivers:readonly RiverPolyline[],falls:readonly RiverFall[],R:number,seaRel:number,terrain:RiverPoolTerrain):{rivers:RiverPolyline[];falls:RiverFall[];features:RiverEscarpment[]} {
 const unchanged=()=>({rivers:[...rivers],falls:[...falls],features:[]}),A=terrain.amplitude;
 for(const anchor of falls){
  if(anchor.nativeCliff||anchor.dropM<6||anchor.half<3.5)continue;
  const route=rivers.find(r=>r.id===anchor.riverId);if(!route||route.canyonId)continue;
  const i=route.points.findIndex(p=>p.s===anchor.lip.s);if(i<4||i+15>=route.points.length)continue;
  const points=route.points.map(p=>({...p})),last=points[i+11];
  const totalDrop=(points[i].ws-last.ws)*A;
  if(totalDrop<48||totalDrop>78||(last.ws-seaRel)*A<10)continue;
  if(points.slice(i-3,i+12).some(p=>p.fx*anchor.lip.fx+p.fy*anchor.lip.fy+p.fz*anchor.lip.fz<.975))continue;
  if(falls.some(f=>f!==anchor&&f.riverId===route.id&&f.end.s>anchor.lip.s-35&&f.lip.s<last.s+45))continue;
  const id=`${anchor.id}:escarpment`,widthM=42+Math.min(8,anchor.half),stages:RiverFall[]=[];
  // Width transitions use the existing route points and cross-section chart.
  // Widths return continuously to the original route below the formation.
  for(let j=Math.max(0,i-4);j<=i+14;j++){
   const k=smooth(points[i-4].s,points[i].s,points[j].s)*(1-smooth(points[i+10].s,points[i+14].s,points[j].s));
   points[j].half*=1+.65*k;
  }
  let fits=true;
  for(const [n,a,b]of [[0,0,4],[1,5,7],[2,9,11]]){
   const lip=points[i+a],end=points[i+b],dropM=(lip.ws-end.ws)*A;
   if(dropM<(n===0?18:8)||dropM>(n===0?28:17)){fits=false;break;}
   const gravity=waterGravity(R),flightTime=Math.sqrt(2*dropM/gravity),runM=lip.speed*flightTime;
   const f:RiverFall={id:n===0?anchor.id:`${anchor.id}:tier:${n}`,riverId:route.id,lip:{...lip},end:{...end},dropM,runM,gravity,flightTime,half:lip.half,escarpmentId:id};
   // A receiver must remain enclosed after widening; bed cuts are bounded.
   for(let row=0;row<=16&&fits;row++){
    const p=riverPointBetween(lip,end,row/16);
    for(const cross of [-p.half*2.6,0,p.half*2.6]){
     const d=shifted(p,R,0,cross),h=terrain.nativeHeight(...d)*A;
     if(terrain.excluded?.(...d)||h-end.ws*A>ESCARPMENT_MAX_CUT_M-6||h<end.ws*A+.35){fits=false;break;}
    }
   }
   if(!fits)break;stages.push(f);
  }
  if(!fits)continue;
  const feature:RiverEscarpment={id,riverId:route.id,anchorId:anchor.id,center:[anchor.lip.x,anchor.lip.y,anchor.lip.z],radiusM:last.s-anchor.lip.s+widthM+24,widthM,stages};
  // Exclude active ice/lava across the full formation, not just the channel.
  for(let along=-8;along<=last.s-anchor.lip.s+12&&fits;along+=8)for(let cross=-widthM;cross<=widthM;cross+=8){
   const d=shifted(anchor.lip,R,along,cross);if(terrain.excluded?.(...d)){fits=false;break;}
  }
  // Do not alter another stream, canyon or confluence inside the bowl.
  for(const other of rivers)if(other.id!==route.id&&other.points.some(p=>pmath.hypot(p.x-anchor.lip.x,p.y-anchor.lip.y,p.z-anchor.lip.z)*R<feature.radiusM+other.points[0].half*3))fits=false;
  if(!fits)continue;
  return{rivers:rivers.map(r=>r.id===route.id?{...r,points}:r),falls:falls.filter(f=>f!==anchor).concat(stages),features:[feature]};
 }
 return unchanged();
}
/** Lower-only terracing with irregular brows, unequal side shelves and finite
 * feathered boundaries. The centre corridor retains the exact waterfall lip. */
export function sampleRiverEscarpments(features:readonly RiverEscarpment[],R:number,A:number,x:number,y:number,z:number,nativeM:number,out:EscarpmentSample):EscarpmentSample {
 out.heightM=nativeM;out.rock=out.terrace=out.weight=0;
 for(const feature of features){
  const dx=(x-feature.center[0])*R,dy=(y-feature.center[1])*R,dz=(z-feature.center[2])*R;
  if(dx*dx+dy*dy+dz*dz>feature.radiusM*feature.radiusM)continue;
  for(const f of feature.stages){
   const p=f.lip,dx=(x-p.x)*R,dy=(y-p.y)*R,dz=(z-p.z)*R;
   const along=dx*p.fx+dy*p.fy+dz*p.fz,cross=dx*(p.fy*p.z-p.fz*p.y)+dy*(p.fz*p.x-p.fx*p.z)+dz*(p.fx*p.y-p.fy*p.x),side=Math.abs(cross),run=f.end.s-f.lip.s;
   if(along<-.5||along>run+12||side>feature.widthM)continue;
   const outer=1-smooth(feature.widthM-12,feature.widthM,side),skew=smooth(f.half*2,f.half*3,side);
   const brow=.35+Math.max(-.2,skew*(1.3+cross*.025+1.4*pmath.sin(side*.17+f.lip.s*.09)));
   const face=smooth(brow,brow+.65,along),ends=1-smooth(run+3,run+12,along),weight=outer*face*ends;
   if(weight<=0)continue;
   // The high shelf is attached to one side of the main face. The other side
   // opens into the low receiving terrace, avoiding a repeated staircase.
   const shoulder=smooth(f.half*2.7,f.half*2.7+3,side)*(cross>0?.34:.18)*(1-smooth(7,17,along));
   const cap=f.end.ws*A+2.8+side*.018+shoulder*f.dropM;
   const cut=Math.min(ESCARPMENT_MAX_CUT_M-6,Math.max(0,nativeM-cap))*weight;
   if(nativeM-cut<out.heightM){out.heightM=nativeM-cut;out.weight=weight;out.rock=outer*ends*(.27+.73*(1-smooth(brow+.6,brow+2.5,along)));out.terrace=weight*smooth(brow+3,brow+6,along)*smooth(.2,1.5,cut);}
  }
 }
 return out;
}
